import { useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import type { Facility, FacilityBooking, FacilityBookingStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, Spinner, StatusBadge } from '../../ui/components';

const BOOKING_STATUSES: FacilityBookingStatus[] = ['PENDING_PAYMENT', 'CONFIRMED', 'CANCELLED', 'COMPLETED'];

/** Default start time for the "new booking" form: tomorrow at 09:00 local. */
function tomorrowAt(hour: number): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * GET /facilities/:id/bookings (that district's own staff — enforced by
 * requireDistrictStaff, see M41's routes/facilities.ts) plus the booking
 * lifecycle: confirm / cancel / complete. No seed data ships any
 * FacilityBooking rows, so "New booking" is included to exercise the real
 * create → confirm → complete chain end to end.
 */
export function BookingsPanel({ facility }: { facility: Facility }) {
  const [status, setStatus] = useState<FacilityBookingStatus | ''>('');
  const bookingsQ = useApiQuery<Page<FacilityBooking>>(
    `/api/v1/m41/facilities/${facility.id}/bookings?limit=100${status ? `&status=${status}` : ''}`,
  );
  const [showNew, setShowNew] = useState(false);
  const [cancelling, setCancelling] = useState<FacilityBooking | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const act = async (id: string, action: 'confirm' | 'complete') => {
    setBusyId(id);
    setActionError(null);
    try {
      await api.post(`/api/v1/m41/bookings/${id}/${action}`);
      bookingsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `Failed to ${action} booking`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-slate-700">Bookings — {facility.name}</h2>
        <div className="flex items-center gap-2">
          <select className="field-input !w-auto !py-1" value={status} onChange={(e) => setStatus(e.target.value as FacilityBookingStatus | '')}>
            <option value="">All statuses</option>
            {BOOKING_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button className="btn-secondary" onClick={() => setShowNew(true)}>
            + New booking
          </button>
        </div>
      </div>

      {actionError ? (
        <div className="mb-2">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {bookingsQ.error ? <ErrorNote message={bookingsQ.error} /> : null}
      {bookingsQ.loading ? (
        <Spinner />
      ) : (bookingsQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No bookings yet" hint="Use “New booking” to record one, or wait for a citizen booking to come in." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Purpose</th>
                <th className="table-head">When</th>
                <th className="table-head text-right">Fee</th>
                <th className="table-head">Status</th>
                <th className="table-head text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookingsQ.data!.items.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">{b.refNo}</td>
                  <td className="table-cell">{b.purpose}</td>
                  <td className="table-cell text-xs">
                    {formatDateTime(b.startAt)} → {formatDateTime(b.endAt)}
                  </td>
                  <td className="table-cell text-right tabular-nums">{scr(b.feeScr)}</td>
                  <td className="table-cell">
                    <StatusBadge status={b.status} />
                    {b.refundNote ? <div className="mt-1 text-[10px] text-amber-600">{b.refundNote}</div> : null}
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-3">
                      {b.status === 'PENDING_PAYMENT' ? (
                        <button
                          className="text-xs font-semibold text-seagreen-600 hover:underline disabled:opacity-50"
                          disabled={busyId === b.id}
                          onClick={() => act(b.id, 'confirm')}
                        >
                          Confirm
                        </button>
                      ) : null}
                      {b.status === 'PENDING_PAYMENT' || b.status === 'CONFIRMED' ? (
                        <button
                          className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-50"
                          disabled={busyId === b.id}
                          onClick={() => setCancelling(b)}
                        >
                          Cancel
                        </button>
                      ) : null}
                      {b.status === 'CONFIRMED' ? (
                        <button
                          className="text-xs font-semibold text-brand-600 hover:underline disabled:opacity-50"
                          disabled={busyId === b.id}
                          onClick={() => act(b.id, 'complete')}
                        >
                          Mark used
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew ? (
        <NewBookingModal
          facility={facility}
          onClose={() => setShowNew(false)}
          onDone={() => {
            setShowNew(false);
            bookingsQ.reload();
          }}
        />
      ) : null}
      {cancelling ? (
        <CancelBookingModal
          booking={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => {
            setCancelling(null);
            bookingsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewBookingModal({ facility, onClose, onDone }: { facility: Facility; onClose: () => void; onDone: () => void }) {
  const [purpose, setPurpose] = useState('');
  const [startAt, setStartAt] = useState(tomorrowAt(9));
  const [endAt, setEndAt] = useState(tomorrowAt(11));
  const [attendeesEstimate, setAttendeesEstimate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m41/facilities/${facility.id}/bookings`, {
        purpose: purpose.trim(),
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        attendeesEstimate: attendeesEstimate.trim() ? Number.parseInt(attendeesEstimate, 10) : undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create booking');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`New booking — ${facility.name}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Recorded under your own staff identity — M41 has no dedicated "book on behalf of a citizen" field yet, so this
          is best used for the district office's own use of the facility.
        </p>
        <Field label="Purpose">
          <input className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Community consultation meeting" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input className="field-input" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
          </Field>
          <Field label="End">
            <input className="field-input" type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
          </Field>
        </div>
        <Field label="Estimated attendees (optional)">
          <input className="field-input" type="number" min="1" value={attendeesEstimate} onChange={(e) => setAttendeesEstimate(e.target.value)} />
        </Field>
        {facility.hourlyRateScr > 0 ? (
          <p className="text-xs text-slate-500">
            This facility charges {scr(facility.hourlyRateScr)}/hr — a SeyPay fee intent is raised automatically and the
            booking starts PENDING_PAYMENT.
          </p>
        ) : (
          <p className="text-xs text-slate-500">No hourly rate on file — this booking will be CONFIRMED immediately.</p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || purpose.trim().length < 2}>
            {busy ? 'Booking…' : 'Create booking'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CancelBookingModal({ booking, onClose, onDone }: { booking: FacilityBooking; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m41/bookings/${booking.id}/cancel`, { reason: reason.trim() || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to cancel booking');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Cancel booking ${booking.refNo}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason (optional)">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Back
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy}>
            {busy ? 'Cancelling…' : 'Cancel booking'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
